<template>
    <div>
        <div class="row">
            <div v-if="loading" class="col-12 mt-5" v-loading="true"></div>
            <div v-else class="col-md-12">
                <card>
                    <template slot="header">
                        <div class="d-flex justify-content-between align-items-center">
                            <div style="display: flex; gap: 20px;">
                                <p>Checkpoints</p>
                                <el-tooltip v-if="userHasPermissions(['pollCheckpointPrintJobs']) && event.pollCheckpoints" :content="true ? 'Disable Polling' : 'Enable Polling'" placement="top">
                                    <el-switch :active-text="pollCheckpoints ? 'Poller Enabled For Current User' : 'Poller Disabled For Current User'"
                                        class="mb-4" v-model="pollCheckpoints" @change="updateCheckpointPollenableStatus" id="polling-switch" />
                                </el-tooltip>
                            </div>
                            <div>
                                <router-link v-if="userHasPermissions(['createCheckpoints'])"
                                    :to="{ name: 'checkpoints-create' }">
                                    <n-button type="default" size="mini">
                                        Create
                                    </n-button>
                                </router-link>
                                <el-button v-if="userHasPermissions(['admin'])" class="ml-2" :loading="regeneratingQRCodes" @click="regenerateQRCodes">
                                    Regenerate QR Codes
                                </el-button>
                            </div>
                        </div>
                    </template>
                    <el-table :data="$store.state.checkpoints" class="header-background" stripe border>
                        <el-table-column label="Name" prop="name" min-width="200px" />
                        <el-table-column label="Type" prop="type" min-width="200px" />
                        <el-table-column label="QR Code" min-width="200px">
                            <template slot-scope="props">
                                <img :src="$store.state.baseUrl + props.row.qr" width="128px"/>
                                <a :href="getQRUrl('checkpoints', props.row._id)" target="_blank">Link</a>
                            </template>
                        </el-table-column>
                        <el-table-column label="Actions" min-width="450px">
                            <template slot-scope="props">
                                <el-tooltip 
                                    v-if="userHasPermissions(['createCheckpoints'])"
                                    content="Clone" 
                                    placement="top">
                                    <el-button
                                        @click="clone(props.row._id)"
                                        class="btn ml-1"
                                        title="Clone"
                                        :loading="loading"
                                    >
                                        <fa icon="copy"/>
                                    </el-button>
                                </el-tooltip>
                                <router-link v-if="userHasPermissions(['viewAndCreateCheckinsForCheckpoints']) && props.row.checkinEnabled"
                                    :to="{ name: 'checkpoints-checkin', params: { type: 'checkpoint', id: props.row._id } }">
                                    <el-tooltip content="Scan" placement="top">
                                        <el-button class="btn ml-1" title="Scan">
                                            <fa :icon="['fas', 'qrcode']" />
                                        </el-button>
                                    </el-tooltip>
                                </router-link>
                                <router-link v-if="userHasPermissions(['editCheckpoints'])"
                                    :to="{ name: 'checkpoints-edit', params: { checkpointId: props.row._id } }">
                                    <el-tooltip content="Edit" placement="top">
                                        <el-button class="btn ml-1">
                                            <fa :icon="['fas', 'pen']" />
                                        </el-button>
                                    </el-tooltip>
                                </router-link>
                                <el-tooltip v-if="userHasPermissions(['createCheckpoints'])" content="Delete"
                                    placement="top">
                                    <el-button @click="remove(props.row._id)" class="btn btn-danger ml-1"
                                        title="Remove">
                                        <fa :icon="['fas', 'trash']" />
                                    </el-button>
                                </el-tooltip>
                                <router-link v-if="userHasPermissions(['viewAndCreateCheckinsForCheckpoints'])" :to="{ name: 'checkpoints-attendees', params:{ checkpointId: props.row._id } }">
                                    <el-tooltip content="Attendees" placement="top">
                                        <el-button class="btn ml-1">
                                            <fa :icon="['fas', 'users']" />
                                        </el-button>
                                    </el-tooltip>
                                </router-link>
                                <router-link v-if="userHasPermissions(['viewAndCreateCheckinsForCheckpoints'])" :to="{ name: 'checkpoints-checkout', params:{ id: props.row._id } }">
                                    <el-tooltip content="Checkout" placement="top">
                                        <el-button class="btn ml-1">
                                            <svg style="width:16px; height: 16px; fill: #fff;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640"><path d="M416 160L480 160C497.7 160 512 174.3 512 192L512 448C512 465.7 497.7 480 480 480L416 480C398.3 480 384 494.3 384 512C384 529.7 398.3 544 416 544L480 544C533 544 576 501 576 448L576 192C576 139 533 96 480 96L416 96C398.3 96 384 110.3 384 128C384 145.7 398.3 160 416 160zM406.6 342.6C419.1 330.1 419.1 309.8 406.6 297.3L278.6 169.3C266.1 156.8 245.8 156.8 233.3 169.3C220.8 181.8 220.8 202.1 233.3 214.6L306.7 288L96 288C78.3 288 64 302.3 64 320C64 337.7 78.3 352 96 352L306.7 352L233.3 425.4C220.8 437.9 220.8 458.2 233.3 470.7C245.8 483.2 266.1 483.2 278.6 470.7L406.6 342.7z"/></svg>
                                        </el-button>
                                    </el-tooltip>
                                </router-link>
                                <el-tooltip v-if="userHasPermissions(['editCheckpoints'])"
                                    :content="props.row.checkinEnabled ? 'Disable Checkin' : 'Enable Checkin'" placement="top">
                                    <el-switch :active-text="props.row.checkinEnabled ? 'Checkin Enabled' : 'Checkin Disabled'"
                                        class="ml-1" v-model="props.row.checkinEnabled" @change="updateCheckinEnableStatus(props.row)" id="checkin-switch" />
                                </el-tooltip>
                                <el-tooltip v-if="userHasPermissions(['pollCheckpointPrintJobs']) && event.pollCheckpoints"
                                    :content="props.row.pollingEnabled ? 'Checkpoint PrintJob Enabled' : 'Checkpoint PrintJob Disabled'" placement="top">
                                    <el-switch :active-text="props.row.pollingEnabled ? 'Checkpoint PrintJob Enabled' : 'Checkpoint PrintJob Disabled'"
                                        class="ml-1" v-model="props.row.pollingEnabled" @change="updatePollingEnableStatus(props.row)" id="polling-switch" />
                                </el-tooltip>
                            </template>
                        </el-table-column>

                        <el-table-column label="Kiosk Mode QR" min-width="200px">
                            <template slot-scope="props">
                                <img :src="$store.state.baseUrl + props.row.kioskQr" width="128px"/>
                                <a :href="getQRUrl('kiosk', props.row._id)" target="_blank">Link</a>
                            </template>
                        </el-table-column>
                        <el-table-column label="Kiosk Mode Actions" min-width="450px">
                            <template slot-scope="props" >
                                <div style="display: flex; flex-direction: column; gap: 15px;">
                                    <el-tooltip v-if="userHasPermissions(['editCheckpoints'])"
                                        :content="props.row.kioskCheckinUsingRegId ? 'Checkin using REG ID' : 'ECheckin using REG ID'" placement="top">
                                        <el-switch :active-text="props.row.kioskCheckinUsingRegId ? 'Checkin using REG ID' : 'Checkin using REG ID'"
                                            class="ml-1" v-model="props.row.kioskCheckinUsingRegId" @change="updateCheckpoint(props.row._id, { kioskCheckinUsingRegId: props.row.kioskCheckinUsingRegId })" id="checkin-switch" />
                                    </el-tooltip>
                                    <el-tooltip v-if="userHasPermissions(['editCheckpoints'])"
                                        :content="props.row.kioskCheckinUsingBadge ? 'Checkin Using Badge' : 'ECheckin Using Badge'" placement="top">
                                        <el-switch :active-text="props.row.kioskCheckinUsingBadge ? 'Checkin Using Badge' : 'Checkin Using Badge'"
                                            class="ml-1" v-model="props.row.kioskCheckinUsingBadge" @change="updateCheckpoint(props.row._id, {  kioskCheckinUsingBadge: props.row.kioskCheckinUsingBadge } ) " id="checkin-switch" />
                                    </el-tooltip>
                                    <el-tooltip v-if="userHasPermissions(['editCheckpoints'])"
                                        :content="props.row.kioskAutoPrintBadge ? 'Autoprint Label on checkin' : 'Autoprint Label on checkin'" placement="top">
                                        <el-switch :active-text="props.row.kioskAutoPrintBadge ? 'Autoprint Label on checkin' : 'Autoprint Label on checkin'"
                                            class="ml-1" v-model="props.row.kioskAutoPrintBadge" @change="updateCheckpoint(props.row._id, { kioskAutoPrintBadge: props.row.kioskAutoPrintBadge })" id="checkin-switch" />
                                    </el-tooltip>
                                    <el-tooltip v-if="userHasPermissions(['editCheckpoints'])"
                                        :content="props.row.kioskAllowBadgePrintingForExternal ? 'Allow Badge Printing For External' : 'Allow Badge Printing For External'" placement="top">
                                        <el-switch :active-text="props.row.kioskAllowBadgePrintingForExternal ? 'Allow Badge Printing For External' : 'Allow Badge Printing For External'"
                                            class="ml-1" v-model="props.row.kioskAllowBadgePrintingForExternal" @change="updateCheckpoint(props.row._id, { kioskAllowBadgePrintingForExternal: props.row.kioskAllowBadgePrintingForExternal })" id="checkin-switch" />
                                    </el-tooltip>
                                    <el-tooltip v-if="userHasPermissions(['editCheckpoints'])"
                                        :content="props.row.kioskLocalPrintOnly ? 'Enable Local Print Only' : 'Enable Local Print Only'" placement="top">
                                        <el-switch :active-text="props.row.kioskLocalPrintOnly ? 'Enable Local Print Only' : 'Enable Local Print Only'"
                                            class="ml-1" v-model="props.row.kioskLocalPrintOnly" @change="updateCheckpoint(props.row._id, { kioskLocalPrintOnly: props.row.kioskLocalPrintOnly })" id="checkin-switch" />
                                    </el-tooltip>
                                    <el-tooltip v-if="userHasPermissions(['editCheckpoints'])"
                                        :content="props.row.kioskPrintUsingRegId ? 'Print Using Reg Id' : 'Print Using Reg Id'" placement="top">
                                        <el-switch :active-text="props.row.kioskPrintUsingRegId ? 'Print Using Reg Id' : 'Print Using Reg Id'"
                                            class="ml-1" v-model="props.row.kioskPrintUsingRegId" @change="updateCheckpoint(props.row._id, { kioskPrintUsingRegId: props.row.kioskPrintUsingRegId })" id="checkin-switch" />
                                    </el-tooltip>
                                    <el-tooltip v-if="userHasPermissions(['editCheckpoints'])"
                                        :content="props.row.kioskPrintUsingBadge ? 'Print Using Badge' : 'Print Using Badge'" placement="top">
                                        <el-switch :active-text="props.row.kioskPrintUsingBadge ? 'Print Using Badge' : 'Print Using Badge'"
                                            class="ml-1" v-model="props.row.kioskPrintUsingBadge" @change="updateCheckpoint(props.row._id, { kioskPrintUsingBadge: props.row.kioskPrintUsingBadge })" id="checkin-switch" />
                                    </el-tooltip>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                </card>
            </div>
        </div>
    </div>
</template>

<script>
import swal from 'sweetalert2'
import { mapState } from 'vuex'
import http from '../../http'
import { event } from 'jquery';

export default {
    data() {
        return {
            loading: false,
            pollCheckpoints: false,
            regeneratingQRCodes: false
        }
    },
    computed: {
        ...mapState([
            'user',
            'event'
        ])
    },
    mounted() {
        this.getCheckpoints()
        let pollCheckpoints = false;

        if (
        this.user &&
        this.user.polling &&
        this.event &&
        this.event._id &&
        this.user.polling[this.event._id]
        ) {
        pollCheckpoints =
            this.user.polling[this.event._id].checkpoints === true;
        }

        this.pollCheckpoints = pollCheckpoints;
    },
    methods: {
        getQRUrl(t, checkpointId){
            return `/evt/${this.event._id}/`+ t +`/${checkpointId}`
        },  
        async regenerateQRCodes(){
            this.regeneratingQRCodes = true
            try {
                await http.post(`/api/checkpoints/regenerate-qr`, { eventId: this.$route.params.eventId });
                this.$notify("QR codes regenerated successfully", "success")
            } catch (error) {
                console.log(error);
                this.$notify({
                    type: 'danger',
                    message: "Something went wrong !"
                })
            }
            this.regeneratingQRCodes = false
        },
        async updateCheckpoint(checkpointId, data){
            try {
                await http.put(`/api/checkpoints/${checkpointId}`, data);
                this.$notify("Checkpoint updated successfully", "success")
            } catch (error) {
                console.log(error);
                this.$notify({
                    type: 'danger',
                    message: "Something went wrong !"
                })
            }
        },
        async updateCheckpointPollenableStatus(){
            try {
                await http.put(`/api/update-user-poll-status`, { checkpoints: this.pollCheckpoints, eventId: this.event._id });
                // await this.$store.dispatch('get', { path: 'events', key: 'event', id: this.$route.params.eventId })
                this.$store.dispatch('getUser')
                this.$notify("Checkpoint Polling status updated successfully", "success")
            } catch (error) {
                console.log(error);
                
                this.pollCheckpoints = !this.pollCheckpoints
                this.$notify({
                    type: 'danger',
                    message: "Something went wrong !"
                })
            }
        },
        async updateCheckinEnableStatus(checkpoint){
            try {
                const res = await http.put('/api/checkpoints/checkin-enabled-status', { checkinEnabled: checkpoint.checkinEnabled, checkpointId: checkpoint._id })
                this.$notify({
                    message: res.data.message,
                    type: "info"
                })
            } catch (error) {
                this.$store.dispatch('handleErrors', error)
                this.$notify({
                    message: "something went wrong",
                    type: "danger"
                })
                checkpoint.checkinEnabled = false
            }
        },
        async updatePollingEnableStatus(checkpoint){
            try {
                const res = await http.put('/api/checkpoints/polling-enabled-status', { pollingEnabled: checkpoint.pollingEnabled, checkpointId: checkpoint._id })
                this.$notify({
                    message: res.data.message,
                    type: "info"
                })
            } catch (error) {
                this.$store.dispatch('handleErrors', error)
                this.$notify({
                    message: "something went wrong",
                    type: "danger"
                })
                checkpoint.pollingEnabled = false
            }
        },
        userHasPermissions(permissions) {
            if (this.user.admin) return true;
            const roles = this.event.adminRoles && this.user._id && this.event.adminRoles[this.user._id] ? this.event.adminRoles[this.user._id] : []
            let valid = true
            permissions.forEach(per => {
                if (!roles[per]) valid = false;
            })
            return valid;
        },
        getCheckpoints: async function () {
            this.loading = true
            try {
                await this.$store.dispatch('getAll', {
                    path: 'checkpoints',
                    params: {
                        filters: {
                            eventId: this.$route.params.eventId
                        }
                    }
                })
            } catch (e) {
                console.log('erorr getting checkpoints')
            }
            this.loading = false
        },
        remove: async function (id) {
            try {
                let { value: confirmed } = await swal({
                    title: `Are you sure you want to delete the checkpoint?`,
                    buttonsStyling: false,
                    showCancelButton: true,
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    confirmButtonClass: 'btn btn-success btn-fill',
                    cancelButtonClass: 'btn btn-default btn-fill'
                })

                if (!confirmed) {
                    return
                }

                this.$store.commit('SET', ['checkpoints', this.$store.state.checkpoints.filter(checkpoint => checkpoint._id !== id)])
                this.$store.dispatch('remove', { path: 'checkpoints', id })
            } catch (e) {
                console.log(e)
                console.log('error deleting checkpoint')
            }
        },
        async clone(checkpointId){
            this.loading = true;
            try {
                const res = await http.post(`/api/checkpoints/clone`, { checkpointId, eventId: this.$route.params.eventId });
                
                this.$notify(res.data.message);
                await this.getCheckpoints()
            } catch (error) {
                this.$store.handleErrors(error);
            }
            this.loading = false;
        }
    }
}
</script>